import React, { useState } from "react";
import { localizedPath } from "../../../core/localePaths";
import { weddingGuidePaths } from "../../../core/weddingGuidePaths";
import { pageGutterPx, responsiveGridColumns } from "../../../core/breakpoints";
import { C } from "../../../core/constants";
import {
  buildFormInputStyle,
  FORM_FIELD_CELL_CLASS,
  formFieldCellStyle,
  formFieldGridClassName,
  formFieldGridStyle,
} from "../../../core/formFields";
import NativePickerInput from "../../../core/NativePickerInput";
import { validatePartnerBirthDates } from "../../../core/partnerBirthForm";
import { APP_FONT_SIZE } from "../../../core/theme";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { gaTrackCta } from "../../../analytics/ga4";
import { useI18n } from "../../i18n/LocaleProvider";
import { buildWeddingCompatibilityPdfHtml } from "../../export/weddingCompatibilityPdf";
import { createPdfFileName, printHtmlAsPdf } from "../../export/pdfUtils";
import { createWeddingGeneratePrediction, fetchPredictionById, scoreWeddingCompatibility } from "../../predictions/api";
import { toPredictionAccessPopup } from "../../predictions/accessError";
import { getFreeQuotaPopupCopy, shouldShowFreeQuotaPopup } from "../../predictions/freeQuota";
import { applyPredictionUserUpdate } from "../../predictions/postSuccessUserUpdate";
import Button from "../../../ui/Button";
import FreeQuotaConfirmPopup from "../../../ui/FreeQuotaConfirmPopup";
import GlassCard from "../../../ui/GlassCard";
import PredictionAccessPopup from "../../../ui/PredictionAccessPopup";

function GoldText({ children, accent }) {
  return <span style={{ color: accent, textShadow: `0 0 18px ${accent}66` }}>{children}</span>;
}

const guideLinkRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  textDecoration: "none",
  padding: "12px 14px",
  borderRadius: 10,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.09)",
};

export default function WeddingCompatibilityPage({ acc, t, replayPredictionId, authUser, onCreditsUpdate, onResetPage }) {
  const { locale } = useI18n();
  const gPaths = React.useMemo(() => weddingGuidePaths(locale), [locale]);
  const fs = APP_FONT_SIZE;
  const isMobile = useIsMobile();
  const gutter = pageGutterPx(isMobile);
  const interpolate = React.useCallback((template, vars = {}) => {
    return Object.entries(vars).reduce((text, [key, value]) => {
      return text.replace(new RegExp(`%\\{${key}\\}`, "g"), String(value ?? ""));
    }, template);
  }, []);
  const weddingCreditCostRaw = Number(authUser?.predictionCosts?.wedding);
  const weddingCreditCost = Number.isFinite(weddingCreditCostRaw) && weddingCreditCostRaw > 0 ? weddingCreditCostRaw : null;
  const analyzeButtonLabel = weddingCreditCost
    ? interpolate(t("wedding_analyze_cta_cost_dynamic"), { credits: weddingCreditCost })
    : t("wedding_analyze_cta");
  const currentDate = React.useMemo(() => new Date(), []);
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const maxYear = currentYear + 4;
  const yearOptions = React.useMemo(
    () => Array.from({ length: maxYear - currentYear + 1 }, (_, i) => currentYear + i),
    [currentYear, maxYear]
  );
  const clampYearToWindow = React.useCallback(
    (inputYear) => {
      if (!Number.isFinite(inputYear)) return currentYear;
      return Math.min(maxYear, Math.max(currentYear, inputYear));
    },
    [currentYear, maxYear]
  );
  const resolveMonthForYear = React.useCallback(
    (inputMonth, targetYear) => {
      const minMonth = targetYear === currentYear ? currentMonth : 1;
      if (!Number.isFinite(inputMonth)) return minMonth;
      return Math.min(12, Math.max(minMonth, inputMonth));
    },
    [currentMonth, currentYear]
  );
  const [bride, setBride] = useState("");
  const [groom, setGroom] = useState("");
  const [month, setMonth] = useState(() => currentMonth);
  const [year, setYear] = useState(() => currentYear);
  const [analyzed, setAnalyzed] = useState(false);
  const [loadingReplay, setLoadingReplay] = useState(() => Boolean(replayPredictionId));
  const [showFreeQuotaPopup, setShowFreeQuotaPopup] = useState(false);
  const [formError, setFormError] = useState("");
  const [scoreLoading, setScoreLoading] = useState(false);
  const [luckyDaysByDay, setLuckyDaysByDay] = useState({});
  const [topDays, setTopDays] = useState([]);
  const [emptyReason, setEmptyReason] = useState(null);
  const [needsReanalyze, setNeedsReanalyze] = useState(false);
  const [pendingPredictionResponse, setPendingPredictionResponse] = useState(null);
  const [showAccessPopup, setShowAccessPopup] = useState(false);
  const [accessPopupCopy, setAccessPopupCopy] = useState({ title: "", message: "" });
  const [isGenerateLockedByEntitlement, setIsGenerateLockedByEntitlement] = useState(false);
  const isReplayMode = Boolean(replayPredictionId);
  const MONTHS = [
    t("month_jan"), t("month_feb"), t("month_mar"), t("month_apr"),
    t("month_may"), t("month_jun"), t("month_jul"), t("month_aug"),
    t("month_sep"), t("month_oct"), t("month_nov"), t("month_dec")
  ];
  const WEEKDAY_LABELS = [
    t("wedding_weekday_sun"),
    t("wedding_weekday_mon"),
    t("wedding_weekday_tue"),
    t("wedding_weekday_wed"),
    t("wedding_weekday_thu"),
    t("wedding_weekday_fri"),
    t("wedding_weekday_sat"),
  ];
  const monthOptions = React.useMemo(
    () => (year === currentYear ? Array.from({ length: 12 - currentMonth + 1 }, (_, i) => currentMonth + i) : Array.from({ length: 12 }, (_, i) => i + 1)),
    [year, currentYear, currentMonth]
  );
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month - 1, 1).getDay();
  const topDaySet = React.useMemo(() => new Set(topDays.map((d) => d.day)), [topDays]);
  const calDays = [];
  for (let i = 0; i < firstDay; i += 1) calDays.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) calDays.push(d);

  function isParsableDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isFinite(parsed.getTime());
  }

  function validateInputs() {
    const code = validatePartnerBirthDates(
      { birthDate: bride },
      { birthDate: groom },
      {
        required: t("wedding_error_required_dob"),
        invalid: t("wedding_error_invalid_dob"),
        future: t("wedding_error_future_dob"),
      }
    );
    return code || "";
  }

  function applyWeddingScorePayload(rawPayload) {
    const luckyMap = rawPayload?.lucky_days_by_day || rawPayload?.luckyDaysByDay || {};
    const normalizedLucky = Object.entries(luckyMap).reduce((memo, [day, score]) => {
      const dayNum = Number(day);
      const scoreNum = Number(score);
      if (Number.isFinite(dayNum) && Number.isFinite(scoreNum)) memo[dayNum] = scoreNum;
      return memo;
    }, {});
    const ranked = (rawPayload?.top_days || rawPayload?.topDays || []).map((entry) => ({
      day: Number(entry.day),
      score: Number(entry.score),
      note: entry.headline || entry.note || t("wedding_note_1"),
      reasons: Array.isArray(entry.reasons) ? entry.reasons.filter(Boolean) : [],
    })).filter((entry) => Number.isFinite(entry.day) && Number.isFinite(entry.score));
    setLuckyDaysByDay(normalizedLucky);
    setTopDays(ranked);
    setEmptyReason(rawPayload?.empty_reason || rawPayload?.emptyReason || null);
  }

  function invalidateAnalysisState() {
    if (!analyzed || isReplayMode) return;
    setAnalyzed(false);
    setNeedsReanalyze(true);
    setLuckyDaysByDay({});
    setTopDays([]);
    setEmptyReason(null);
  }

  React.useEffect(() => {
    const correctedMonth = resolveMonthForYear(month, year);
    if (correctedMonth !== month) setMonth(correctedMonth);
  }, [month, year, resolveMonthForYear]);

  React.useEffect(() => {
    if (!replayPredictionId) return;
    let cancelled = false;
    async function loadReplayPrediction() {
      setLoadingReplay(true);
      try {
        const response = await fetchPredictionById(replayPredictionId);
        if (cancelled) return;
        const prediction = response?.prediction;
        if (!prediction || prediction.feature !== "wedding") return;
        const payload = prediction.input_payload || {};
        const resultPayload = prediction.result_payload || {};
        const replayYear = clampYearToWindow(Number(payload.year));
        const replayMonth = resolveMonthForYear(Number(payload.month), replayYear);
        setBride(payload.bride || bride);
        setGroom(payload.groom || groom);
        setYear(replayYear);
        setMonth(replayMonth);
        applyWeddingScorePayload(resultPayload);
        setNeedsReanalyze(false);
        setAnalyzed(true);
      } catch (_) {
        // fallback to default mode
      } finally {
        if (!cancelled) setLoadingReplay(false);
      }
    }
    loadReplayPrediction();
    return () => {
      cancelled = true;
    };
  }, [replayPredictionId, clampYearToWindow, resolveMonthForYear]);

  React.useEffect(() => {
    if (isReplayMode || !pendingPredictionResponse || scoreLoading || !analyzed) return;
    applyPredictionUserUpdate({ authUser, predictionResponse: pendingPredictionResponse, onCreditsUpdate });
    setPendingPredictionResponse(null);
  }, [isReplayMode, pendingPredictionResponse, scoreLoading, analyzed, authUser, onCreditsUpdate]);

  React.useEffect(() => {
    if (isReplayMode || !isGenerateLockedByEntitlement) return;
    if (!shouldShowFreeQuotaPopup(authUser, "wedding")) {
      setIsGenerateLockedByEntitlement(false);
    }
  }, [authUser, isGenerateLockedByEntitlement, isReplayMode]);

  async function runAnalyzeAndTrack() {
    if (scoreLoading || isGenerateLockedByEntitlement) return;
    if (!isReplayMode) {
      try {
        setScoreLoading(true);
        const scorePayload = await scoreWeddingCompatibility({ bride, groom, month, year });
        applyWeddingScorePayload(scorePayload);

        const predictionResponse = await createWeddingGeneratePrediction({
          input_payload: { bride, groom, month, year },
          result_payload: { analyzed: true, ...scorePayload },
        });
        setPendingPredictionResponse(predictionResponse);
        setIsGenerateLockedByEntitlement(false);
      } catch (error) {
        const popupPayload = toPredictionAccessPopup(error, t);
        if (popupPayload.shouldShowPopup) {
          setAccessPopupCopy({ title: popupPayload.title, message: popupPayload.message });
          setShowAccessPopup(true);
          setIsGenerateLockedByEntitlement(true);
          return;
        }
        setFormError(t("wedding_error_score_failed"));
        return;
      } finally {
        setScoreLoading(false);
      }
    }
    setNeedsReanalyze(false);
    setAnalyzed(true);
  }

  function analyzeAndTrack() {
    if (scoreLoading || isGenerateLockedByEntitlement) return;
    const validationError = validateInputs();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError("");
    if (!isReplayMode && shouldShowFreeQuotaPopup(authUser, "wedding")) {
      setShowFreeQuotaPopup(true);
      return;
    }
    runAnalyzeAndTrack();
  }

  function exportWeddingReadingToPdf() {
    if (!analyzed || scoreLoading) return;
    const now = new Date();
    const exportFileName = createPdfFileName("wedding-compatibility", now);
    const exportedAt = now.toLocaleString();
    const monthLabel = MONTHS[month - 1];
    const topDaysForPdf = topDays.map((item) => ({
      day: Number(item.day),
      score: Number(item.score),
      note: item.note || "",
      reasons: item.reasons || [],
    })).filter((item) => Number.isFinite(item.day) && Number.isFinite(item.score));
    const html = buildWeddingCompatibilityPdfHtml({
      exportedAt,
      brideDob: bride,
      groomDob: groom,
      monthLabel,
      year,
      weekdayLabels: WEEKDAY_LABELS,
      calendarDays: calDays,
      luckyDaysByDay,
      topDays: topDaysForPdf,
      emptyState: topDaysForPdf.length ? null : {
        title: t("wedding_empty_title"),
        desc: t("wedding_empty_desc"),
        hint: emptyReason ? t("wedding_empty_hint_next_month") : t("wedding_empty_hint"),
      },
      theme: {
        primary: acc.primary,
        secondary: acc.secondary,
        tertiary: acc.tertiary,
      },
      labels: {
        report: t("pdf_report_title"),
        title: t("pdf_wedding_title"),
        eyebrow: t("pdf_wedding_eyebrow"),
        exported: t("pdf_exported"),
        brideDob: t("wedding_bride_dob"),
        groomDob: t("wedding_groom_dob"),
        focusMonth: t("pdf_wedding_focus_month"),
        calendarSection: t("pdf_wedding_calendar_section"),
        topDaysSection: t("pdf_wedding_top_days_section"),
        generatedBy: t("pdf_generated_by"),
      },
    });
    printHtmlAsPdf({ html, title: exportFileName.replace(/\.pdf$/i, "") });
  }

  const fieldBorder = formError ? "rgba(255,120,120,0.55)" : "rgba(255,255,255,0.12)";
  const weddingFieldStyle = buildFormInputStyle({
    fontSize: fs,
    extra: { padding: "10px 16px", border: `1px solid ${fieldBorder}` },
  });

  return (
    <div style={{ padding: 32 }}>
      <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 40, fontWeight: 400, color: C.textPrimary, marginBottom: 8 }}>{t("wedding_title_prefix")} <GoldText accent={acc.secondary}>{t("wedding_title_highlight")}</GoldText></h1>
      <p style={{ color: C.textSecondary, fontSize: fs, marginBottom: 20 }}>{t("wedding_subtitle")}</p>
      <GlassCard style={{ padding: "26px 24px", marginBottom: 28 }} accent={acc.tertiary}>
        <div
          style={{
            fontSize: 12,
            letterSpacing: "0.14em",
            color: acc.primary,
            marginBottom: 10,
          }}
        >
          {t("wedding_guide_resources_eyebrow")}
        </div>
        <h2
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: Math.min(28, fs + 10),
            fontWeight: 400,
            color: C.textPrimary,
            lineHeight: 1.2,
            marginBottom: 10,
          }}
        >
          {t("wedding_guide_resources_title")}
        </h2>
        <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.7, marginBottom: 18, maxWidth: 640 }}>
          {t("wedding_guide_resources_intro")}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {[
            ["wedding_tool_guide_dates", "wedding_guide_link_dates", gPaths.auspicious],
            ["wedding_tool_guide_lunar", "wedding_guide_link_lunar", gPaths.lunar],
            ["wedding_tool_guide_elements", "wedding_guide_link_elements", gPaths.elements],
          ].map(([ctaId, labelKey, href], idx) => (
            <a
              key={ctaId}
              href={href}
              style={{
                ...guideLinkRowSx,
                marginBottom: idx === 2 ? 0 : 8,
              }}
              onClick={() =>
                gaTrackCta({
                  cta_id: ctaId,
                  cta_label: t(labelKey),
                  destination: href,
                  screen_class: "wedding",
                })
              }
            >
              <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span style={{ color: acc.primary, fontSize: 12, opacity: 0.9 }} aria-hidden="true">
                  ✦
                </span>
                <span style={{ color: C.textPrimary, fontSize: fs - 1, fontWeight: 500, lineHeight: 1.35 }}>{t(labelKey)}</span>
              </span>
              <span
                style={{
                  flexShrink: 0,
                  color: acc.secondary,
                  fontSize: 11,
                  letterSpacing: "0.06em",
                  border: `1px solid ${acc.secondary}66`,
                  borderRadius: 999,
                  padding: "5px 12px",
                }}
              >
                {t("wedding_guide_link_pill")}
              </span>
            </a>
          ))}
        </div>
      </GlassCard>
      <GlassCard style={{ padding: 32, marginBottom: 32 }} glow accent={acc.secondary}>
        <div
          className={formFieldGridClassName({ wide: true, stacked: isMobile })}
          style={{ ...formFieldGridStyle({ wide: true, stacked: isMobile }), marginBottom: 28 }}
        >
          {[
              { label: t("wedding_bride_dob"), val: bride, set: setBride },
              { label: t("wedding_groom_dob"), val: groom, set: setGroom },
            ].map((f, i) => (
              <div key={i} className={FORM_FIELD_CELL_CLASS} style={formFieldCellStyle()}>
                <div style={{ fontSize: 12, color: acc.secondary, letterSpacing: "0.1em" }}>
                  {f.label.toUpperCase()}
                </div>
                <NativePickerInput
                  type="date"
                  value={f.val}
                  onChange={(e) => {
                    f.set(e.target.value);
                    setFormError("");
                    invalidateAnalysisState();
                  }}
                  disabled={isReplayMode}
                  style={weddingFieldStyle}
                />
              </div>
          ))}
          <div className={FORM_FIELD_CELL_CLASS} style={formFieldCellStyle()}>
            <div style={{ fontSize: 12, color: acc.secondary, letterSpacing: "0.1em" }}>
              {t("wedding_month_label")}
            </div>
            <select
              value={month}
              onChange={(e) => {
                setMonth(+e.target.value);
                invalidateAnalysisState();
              }}
              style={weddingFieldStyle}
              disabled={isReplayMode}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {MONTHS[m - 1]}
                </option>
              ))}
            </select>
          </div>
          <div className={FORM_FIELD_CELL_CLASS} style={formFieldCellStyle()}>
            <div style={{ fontSize: 12, color: acc.secondary, letterSpacing: "0.1em" }}>
              {t("wedding_year_label")}
            </div>
            <select
              value={year}
              onChange={(e) => {
                setYear(+e.target.value);
                invalidateAnalysisState();
              }}
              style={weddingFieldStyle}
              disabled={isReplayMode}
            >
              {yearOptions.map((optionYear) => (
                <option key={optionYear}>{optionYear}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="ecliptica-form-after" style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: C.textSecondary }}>{t("wedding_window_desc_1")}</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>{t("wedding_window_desc_2")}</div>
        </div>
        {formError ? (
          <div style={{ marginBottom: 14, fontSize: 12, color: "#ff8e9f" }}>{formError}</div>
        ) : null}
        {needsReanalyze ? (
          <div style={{ marginBottom: 14, fontSize: 12, color: C.textSecondary }}>{t("wedding_reanalyze_needed_hint")}</div>
        ) : null}
        {!isReplayMode ? (
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <Button variant="primary" acc={acc} onClick={analyzeAndTrack} disabled={scoreLoading || isGenerateLockedByEntitlement} style={{ padding: "13px 40px", fontSize: fs }}>
              {scoreLoading ? t("wedding_loading_analysis") : analyzeButtonLabel}
            </Button>
            {analyzed && !scoreLoading ? (
              <div style={{ fontSize: 13, color: C.textMuted }}>
                <span style={{ color: acc.primary }}>✦</span> {interpolate(t("wedding_credits_used_dynamic"), { credits: weddingCreditCost || 10 })}
              </div>
            ) : null}
            {analyzed && !scoreLoading ? (
              <Button variant="outline" acc={acc} onClick={exportWeddingReadingToPdf}>{t("export_result_pdf")}</Button>
            ) : null}
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ color: C.textMuted, fontSize: 13 }}>
              <span style={{ color: acc.primary }}>✦</span> {loadingReplay ? t("wedding_loading_replay") : t("dashboard_replay_mode_badge")}
            </div>
            {!loadingReplay && (
              <Button
                variant="primary"
                acc={acc}
                onClick={() => (onResetPage ? onResetPage() : window.location.assign(localizedPath("wedding", locale)))}
              >
                {t("dashboard_replay_start_new_wedding")}
              </Button>
            )}
            {analyzed && !scoreLoading ? (
              <Button variant="outline" acc={acc} onClick={exportWeddingReadingToPdf}>{t("export_result_pdf")}</Button>
            ) : null}
          </div>
        )}
      </GlassCard>
      {analyzed && (
        <div style={{ display: "grid", gridTemplateColumns: responsiveGridColumns(isMobile, { desktop: "1fr 320px", mobile: "1fr" }), gap: 28, alignItems: "start" }}>
          <GlassCard style={{ padding: isMobile ? 16 : 28 }} glow accent={acc.primary}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 20 : 24, color: C.textPrimary, marginBottom: 20, textAlign: "center" }}>{MONTHS[month - 1]} {year}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: isMobile ? 4 : 6, marginBottom: 6 }}>
              {WEEKDAY_LABELS.map((label, idx) => (
                <div key={`weekday-${idx}`} style={{ fontSize: 11, color: C.textMuted, textAlign: "center" }}>{label}</div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: isMobile ? 4 : 6 }}>{calDays.map((d, i) => { if (!d) return <div key={i} />; const pct = luckyDaysByDay[d]; const isTop = topDaySet.has(d); const hasScore = Number.isFinite(pct); return <div key={i} style={{ aspectRatio: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRadius: 10, background: isTop ? `linear-gradient(135deg,${acc.secondary}33,${acc.primary}22)` : hasScore ? `${acc.primary}12` : "transparent", border: isTop ? `1px solid ${acc.secondary}77` : hasScore ? `1px solid ${acc.primary}33` : "1px solid transparent" }}><span style={{ fontSize: 13, color: isTop ? C.textPrimary : hasScore ? acc.primary : C.textMuted }}>{d}</span>{isTop ? <span style={{ fontSize: 9 }}>⭐</span> : hasScore ? <span style={{ fontSize: 8, color: acc.primary }}>{pct}%</span> : null}</div>; })}</div>
          </GlassCard>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {topDays.length ? topDays.map((d, i) => (
              <GlassCard key={i} hoverable style={{ padding: 20 }} accent={acc.secondary} glow>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div>
                    <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: C.textPrimary }}>{MONTHS[month - 1]} {d.day}, {year}</div>
                    <div style={{ fontSize: 13, color: C.textPrimary, marginTop: 6, lineHeight: 1.5 }}>{d.note}</div>
                  </div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: acc.secondary }}>{d.score}%</div>
                </div>
                {!!d.reasons?.length && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 10, letterSpacing: "0.1em", color: acc.secondary, marginBottom: 6 }}>{t("wedding_reasons_label")}</div>
                    {d.reasons.map((reason, reasonIndex) => (
                      <div key={`reason-${i}-${reasonIndex}`} style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.55, marginBottom: 6 }}>• {reason}</div>
                    ))}
                  </div>
                )}
              </GlassCard>
            )) : (
              <GlassCard style={{ padding: 20 }} accent={acc.secondary} glow>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary, marginBottom: 6 }}>{t("wedding_empty_title")}</div>
                <div style={{ fontSize: 12, color: C.textSecondary }}>{t("wedding_empty_desc")}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>{emptyReason ? t("wedding_empty_hint_next_month") : t("wedding_empty_hint")}</div>
              </GlassCard>
            )}
          </div>
        </div>
      )}
      <FreeQuotaConfirmPopup
        open={showFreeQuotaPopup}
        title={getFreeQuotaPopupCopy("wedding", t).title}
        message={getFreeQuotaPopupCopy("wedding", t).message}
        note={getFreeQuotaPopupCopy("wedding", t).note}
        confirmLabel={t("free_quota_popup_confirm")}
        cancelLabel={t("free_quota_popup_cancel")}
        onClose={() => setShowFreeQuotaPopup(false)}
        onConfirm={() => {
          setShowFreeQuotaPopup(false);
          runAnalyzeAndTrack();
        }}
        acc={acc}
      />
      <PredictionAccessPopup
        open={showAccessPopup}
        title={accessPopupCopy.title}
        message={accessPopupCopy.message}
        onClose={() => setShowAccessPopup(false)}
        onGoToPricing={() => { window.location.href = localizedPath("creditsPlans", locale); }}
        acc={acc}
      />
    </div>
  );
}
